import { type FC, useState } from 'react';
import { Archive, ArchiveRestore, CheckCircle, Edit, Eye, Lock, LockOpen, MoreVertical } from 'lucide-react';
import { NavLink as ReactRouterLink, useNavigate } from 'react-router-dom';
import { canArchivarEscrituras, checkAllPermissions, PERMISSIONS } from '@/auth/permissions';
import { useAuth } from '@/auth';
import { TableCell } from './ui/table';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';


export interface TableCellActionsProps {
  tramite: any;
  onClickFinalizar?: (id: number) => () => void;
  onClickBloqueo?: (tramite: any) => void;
  onClickArchivado?: (tramite: any) => void;
}

const TableCellActions: FC<TableCellActionsProps> = ({
  tramite,
  onClickFinalizar,
  onClickBloqueo,
  onClickArchivado,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const userPermissions = user?.permissions || [];
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const canFinalizar = checkAllPermissions(userPermissions, [
    PERMISSIONS.MANAGE_TRAMITES.name,
  ]) && tramite.estado !== 'finalizado';

  const canEditar = checkAllPermissions(userPermissions, [
    PERMISSIONS.MANAGE_TRAMITES.name,
  ]);

  const canBloquear = checkAllPermissions(userPermissions, [
    PERMISSIONS.MANAGE_BLOQUEO_TRAMITE.name,
  ]);

  const canArchivar = canArchivarEscrituras(userPermissions);

  return (
    <TableCell>
      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
          >
            <MoreVertical className="h-4 w-4" />
            <span className="sr-only">Abrir menú de acciones</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={() => navigate('/dashboard', { state: { tramite } })}
          >
            <Eye className="h-4 w-4" />
            Ver trámite
          </DropdownMenuItem>
          {canEditar && (
            <DropdownMenuItem
              onSelect={() => navigate('/tramite', { state: { tramite } })}
            >
              <Edit className="h-4 w-4" />
              Editar trámite
            </DropdownMenuItem>
          )}
          {canFinalizar && (
            <DropdownMenuItem
              onSelect={onClickFinalizar(tramite.id)}
            >
              <CheckCircle className="h-4 w-4" />
              Finalizar trámite
            </DropdownMenuItem>
          )}
          {canBloquear && (
            <DropdownMenuItem
              onSelect={() => {
                // Cerrar el dropdown y diferir la apertura del diálogo de bloqueo:
                // si el Dialog (Radix) se abre en el mismo tick que se cierra el
                // DropdownMenu, ambos pisan `pointer-events` de <body> y la página
                // queda inutilizable hasta refrescar.
                setDropdownOpen(false);
                setTimeout(() => onClickBloqueo?.(tramite), 100);
              }}
            >
              {tramite.bloqueado ? <LockOpen className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
              {tramite.bloqueado ? 'Desbloquear escritura' : 'Marcar como bloqueada'}
            </DropdownMenuItem>
          )}
          {canArchivar && (
            <DropdownMenuItem
              onSelect={() => {
                // Mismo diferido que el bloqueo: abrir un Dialog de Radix en el
                // mismo tick en que se cierra el DropdownMenu deja la página sin
                // `pointer-events` hasta refrescar.
                setDropdownOpen(false);
                setTimeout(() => onClickArchivado?.(tramite), 100);
              }}
            >
              {tramite.archivado ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
              {tramite.archivado ? 'Desarchivar escritura' : 'Archivar escritura'}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </TableCell>
  );
};

TableCellActions.displayName = 'TableCellActions';

export default TableCellActions;